import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["native", "trigger", "triggerText", "dropdown", "optionsList", "search", "error"]
  static values = { search: Boolean, errorMessage: String }

  connect() {
    this.isOpen = false
    this.highlightedIndex = -1
    this.optionData = this.buildOptionData()

    this.outsideClickHandler = (event) => this.handleOutsideClick(event)
    this.resizeHandler = () => this.handleResize()
    this.rebuildHandler = () => this.rebuild()

    document.addEventListener("click", this.outsideClickHandler)
    window.addEventListener("resize", this.resizeHandler)
    this.element.addEventListener("select-field:rebuild", this.rebuildHandler)

    this.updateTrigger()
    this.renderOptions(this.optionData)
  }

  rebuild() {
    this.optionData = this.buildOptionData()
    this.updateTrigger()
    this.renderOptions(this.optionData)
  }

  disconnect() {
    document.removeEventListener("click", this.outsideClickHandler)
    window.removeEventListener("resize", this.resizeHandler)
    this.element.removeEventListener("select-field:rebuild", this.rebuildHandler)
    this.unlockBody()
  }

  buildOptionData() {
    return Array.from(this.nativeTarget.options).map((option, index) => {
      if (option.hidden) return null

      return {
        index,
        value: option.value,
        text: option.text,
        selected: option.selected,
        disabled: option.disabled,
        dataset: { ...option.dataset }
      }
    }).filter(Boolean)
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.isOpen) return

    this.isOpen = true
    this.element.classList.add("select-field--open")
    this.triggerTarget.setAttribute("aria-expanded", "true")

    if (this.isMobile()) {
      this.dropdownTarget.classList.add("select-field__dropdown--mobile")
      this.lockBody()
    }

    this.highlightSelected()
    this.focusSearch()
  }

  close() {
    if (!this.isOpen) return

    this.isOpen = false
    this.element.classList.remove("select-field--open")
    this.triggerTarget.setAttribute("aria-expanded", "false")
    this.dropdownTarget.classList.remove("select-field__dropdown--mobile")
    this.unlockBody()

    if (this.hasSearchTarget) {
      this.searchTarget.value = ""
      this.filter()
    }

    this.highlightedIndex = -1
    this.clearHighlight()
  }

  isMobile() {
    return window.innerWidth < 640
  }

  handleResize() {
    if (!this.isOpen) return

    if (this.isMobile()) {
      this.dropdownTarget.classList.add("select-field__dropdown--mobile")
      this.lockBody()
    } else {
      this.dropdownTarget.classList.remove("select-field__dropdown--mobile")
      this.unlockBody()
    }
  }

  lockBody() {
    document.body.classList.add("select-field-body-locked")
  }

  unlockBody() {
    document.body.classList.remove("select-field-body-locked")
  }

  handleOutsideClick(event) {
    if (!this.isOpen) return
    if (this.element.contains(event.target)) return
    this.close()
  }

  focusSearch() {
    if (this.hasSearchTarget) {
      setTimeout(() => this.searchTarget.focus(), 10)
    }
  }

  select(event) {
    event.preventDefault()
    event.stopPropagation()

    const value = event.currentTarget.dataset.value
    this.setValue(value)
  }

  setValue(value) {
    this.nativeTarget.value = value
    this.nativeTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.updateTrigger()
    this.clearError()
    this.close()
  }

  updateTrigger() {
    const selected = this.nativeTarget.selectedOptions[0]
    if (selected && selected.value !== "") {
      this.triggerTextTarget.textContent = selected.text
      this.triggerTextTarget.classList.remove("select-field__trigger-text--placeholder")
    } else {
      this.triggerTextTarget.textContent = this.placeholderText()
      this.triggerTextTarget.classList.add("select-field__trigger-text--placeholder")
    }
  }

  placeholderText() {
    return this.nativeTarget.querySelector("option[value='']")?.text ||
      this.triggerTextTarget.dataset.placeholder ||
      "Выберите..."
  }

  filter() {
    if (!this.hasSearchTarget) return

    const query = this.searchTarget.value.trim().toLowerCase()
    const filtered = query === ""
      ? this.optionData
      : this.optionData.filter((option) => option.text.toLowerCase().includes(query))

    this.renderOptions(filtered)
    this.highlightedIndex = -1
  }

  renderOptions(options) {
    this.optionsListTarget.innerHTML = ""

    if (options.length === 0) {
      const empty = document.createElement("li")
      empty.className = "select-field__empty"
      empty.textContent = "Ничего не найдено"
      this.optionsListTarget.appendChild(empty)
      return
    }

    options.forEach((option) => {
      const li = document.createElement("li")
      const button = document.createElement("button")
      button.type = "button"
      button.className = "select-field__option"
      button.textContent = option.text
      button.dataset.value = option.value
      button.dataset.index = option.index
      button.dataset.action = "click->select-field#select mouseenter->select-field#highlightIndex"
      button.setAttribute("role", "option")

      if (option.value === this.nativeTarget.value) {
        button.classList.add("select-field__option--selected")
        button.setAttribute("aria-selected", "true")
      } else {
        button.setAttribute("aria-selected", "false")
      }

      if (option.disabled) {
        button.disabled = true
        button.classList.add("select-field__option--disabled")
      }

      li.appendChild(button)
      this.optionsListTarget.appendChild(li)
    })
  }

  highlightIndex(event) {
    const index = parseInt(event.currentTarget.dataset.index, 10)
    if (Number.isNaN(index)) return

    this.highlightedIndex = index
    this.applyHighlight()
  }

  highlightSelected() {
    const selectedIndex = this.nativeTarget.selectedIndex
    if (selectedIndex >= 0) {
      this.highlightedIndex = selectedIndex
      this.applyHighlight()
    }
  }

  handleTriggerKeydown(event) {
    switch (event.key) {
      case "ArrowDown":
      case "Enter":
      case " ":
        event.preventDefault()
        this.open()
        break
      case "Escape":
        this.close()
        break
    }
  }

  handleSearchKeydown(event) {
    const visibleOptions = this.visibleOptions()
    if (visibleOptions.length === 0) return

    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        this.highlightNext(visibleOptions)
        break
      case "ArrowUp":
        event.preventDefault()
        this.highlightPrevious(visibleOptions)
        break
      case "Enter":
        event.preventDefault()
        this.selectHighlighted()
        break
      case "Escape":
        event.preventDefault()
        this.close()
        break
    }
  }

  visibleOptions() {
    return Array.from(this.optionsListTarget.querySelectorAll(".select-field__option:not(:disabled)"))
  }

  highlightNext(visibleOptions) {
    this.highlightedIndex = this.nextEnabledIndex(this.highlightedIndex + 1, visibleOptions)
    this.applyHighlight()
  }

  highlightPrevious(visibleOptions) {
    this.highlightedIndex = this.previousEnabledIndex(this.highlightedIndex - 1, visibleOptions)
    this.applyHighlight()
  }

  nextEnabledIndex(start, visibleOptions) {
    for (let i = start; i < this.optionData.length; i++) {
      if (this.optionData[i] && !this.optionData[i].disabled && this.optionVisible(visibleOptions, this.optionData[i].index)) {
        return this.optionData[i].index
      }
    }
    return this.highlightedIndex
  }

  previousEnabledIndex(start, visibleOptions) {
    for (let i = start; i >= 0; i--) {
      if (this.optionData[i] && !this.optionData[i].disabled && this.optionVisible(visibleOptions, this.optionData[i].index)) {
        return this.optionData[i].index
      }
    }
    return this.highlightedIndex
  }

  optionVisible(visibleOptions, index) {
    return visibleOptions.some((el) => parseInt(el.dataset.index, 10) === index)
  }

  applyHighlight() {
    this.clearHighlight()
    if (this.highlightedIndex < 0) return

    const button = this.optionsListTarget.querySelector(`[data-index="${this.highlightedIndex}"]`)
    if (!button) return

    button.classList.add("select-field__option--highlighted")
    button.scrollIntoView({ block: "nearest" })
  }

  clearHighlight() {
    this.optionsListTarget.querySelectorAll(".select-field__option--highlighted").forEach((el) => {
      el.classList.remove("select-field__option--highlighted")
    })
  }

  selectHighlighted() {
    if (this.highlightedIndex < 0) return
    const option = this.optionData[this.highlightedIndex]
    if (!option || option.disabled) return
    this.setValue(option.value)
  }

  showError(message) {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = message
    this.element.classList.add("select-field--error")
  }

  clearError() {
    if (!this.hasErrorTarget) return
    this.errorTarget.textContent = ""
    this.element.classList.remove("select-field--error")
  }
}
